import { MapBrowserEvent } from "ol";
import Overlay from "ol/Overlay";
import { useContext, useState } from "react";
import { useDeepCompareEffectNoCheck } from "use-deep-compare-effect";

import { ProjectTypeConstants } from "../../../../constants";
import { LocationAndGisFileData } from "../../../../models";
import { getCenterFromCoordinates } from "../../../../widget/general/Map/MapOpenLayer/utils";
import { LandholdingContext } from "../LandholdingContext";

interface UseLocationReturnData {
  locationData: LocationAndGisFileData[];
  hoverOverlay?: Overlay;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  pointermoveFunc?: (event: MapBrowserEvent<any>) => void;
  centerCoordinate: { lat: number; lng: number };
}

export const useLocation = (): UseLocationReturnData => {
  const { landholdingDetails } = useContext(LandholdingContext);
  const [locationData, setLocationData] = useState<LocationAndGisFileData[]>([]);
  const [centerCoordinate, setCenterCoordinate] = useState<{ lat: number; lng: number }>({
    lat: 51.509865,
    lng: -0.118092,
  }); // Default center coordinate LONDON 51.509865, -0.118092.
  const overLayId = "info-overlay";
  const infoElement = document.createElement("div");
  infoElement.className = "info-overlay";
  const hoverOverlay = new Overlay({
    id: overLayId,
    element: infoElement,
    positioning: "bottom-left",
    stopEvent: false,
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const pointermoveFunc = (event: MapBrowserEvent<any>): void => {
    const infoDiv = event.map.forEachFeatureAtPixel(event.pixel, (f, l) => {
      // eslint-disable-next-line no-restricted-syntax
      const infoOverlay = event.map.getOverlayById(overLayId);
      const projectName = l.get("projectName");
      const areaNetHa = l.get("areaNetHa");
      if (projectName) {
        if (!infoOverlay) return null;
        infoOverlay.setPosition(event.coordinate);
        infoElement.innerHTML = `<h4>${projectName}</h4><p>${areaNetHa} ha</p>`;
        infoElement.style.display = "block";
        infoElement.style.width = "max-content";
        return infoElement;
      }
      return null;
    });
    if (!infoDiv) {
      infoElement.style.display = "none";
    }
  };

  useDeepCompareEffectNoCheck(() => {
    setLocationData(
      landholdingDetails?.projects.map((projectDetails) => ({
        addressCountryCode: projectDetails?.addressCountryCode,
        gridReference: projectDetails?.locationGridReference,
        latitude: projectDetails?.locationCoordinates?.split(",")[0] ?? null,
        longitude: projectDetails?.locationCoordinates?.split(",")[1] ?? null,
        locationUuid: projectDetails?.projectLocation?.uuid ?? null,
        locationRowVersion: projectDetails?.projectLocation?.rowVersion ?? null,
        boundaryData: projectDetails?.projectLocation?.boundaryData ?? null,
        shapeFileUrl: projectDetails?.projectLocation?.locationFileUrl ?? undefined,
        gisFile: {
          projectShape: {
            geoJsonFileUrl: projectDetails?.projectLocation?.locationFileUrl ?? undefined,
            geoJsonContent: projectDetails?.projectLocation?.boundaryData,
            fillColor: ProjectTypeConstants.PROJECT_TYPE_CHART_COLOR_MAP[projectDetails.projectType.displayName][0],
          },
          vectorFile: undefined,
          rasterLayer: undefined,
          extraInfo: {
            projectName: projectDetails?.name,
            areaNetHa: typeof projectDetails.areaNetHa === "number" ? projectDetails.areaNetHa : undefined,
          },
        },
      })) ?? []
    );
    const coordinatesList: { lat: number; lng: number }[] = [];
    landholdingDetails?.projects.forEach((projectDetails) => {
      const coordinates = projectDetails?.locationCoordinates?.split(",").map(Number);
      if (coordinates && coordinates.length === 2) {
        const [lat, lng] = coordinates;
        coordinatesList.push({ lat, lng });
      }
    });
    const calculatedCenterCoordinate = getCenterFromCoordinates(coordinatesList);
    setCenterCoordinate(calculatedCenterCoordinate);
  }, [landholdingDetails]);

  return {
    locationData,
    hoverOverlay,
    pointermoveFunc,
    centerCoordinate,
  };
};
